import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { GmrDataService } from '../core/gmr-data.service';
import { getUnavailableAlert } from '../core/unavailable-alert';
import { UiStateService } from '../core/ui-state.service';
import { DecodeEntitiesPipe } from '../shared/pipes/decode-entities.pipe';

interface LanguageOption
{
  code: string;
  label: string;
}

//
// 1:1 port of the top header/nav in menu.html (app launcher menu, logo/back link, language
// selector, contact/help links, profile panel).
//
@Component({
  selector: 'app-top-nav',
  standalone: true,
  imports: [TranslateDirective, TranslatePipe, DecodeEntitiesPipe],
  //
  // Root template has two sibling <header> elements; without an explicit host display the
  // custom element defaults to display:inline and both headers collapse into inline flow.
  //
  styles: [':host { display: block; }'],
  templateUrl: './top-nav.component.html',
})
export class TopNavComponent
{
  protected readonly data = inject(GmrDataService);
  protected readonly ui = inject(UiStateService);
  private readonly translate = inject(TranslateService);

  protected readonly myInfo = computed(() => this.data.myInfo());
  protected readonly unavailableAlert = computed(() => getUnavailableAlert(this.data.unavailableStats()));

  //
  // The language the page is shown in, which is what the selector has to say: the profile of the user
  // sets it when the catalogue arrives, long after the selector was drawn, and an error before that
  // shows English texts above a selector which would still say Dutch.
  //
  protected readonly language = toSignal
  (
    this.translate.onLangChange.pipe(map(event => event.lang))
  , { initialValue: this.translate.getCurrentLang() ?? this.translate.getFallbackLang() ?? 'en' }
  );

  //
  // Matches the original <select id="language"> option list.
  //
  readonly languages: LanguageOption[] =
  [
    { code: 'en', label: 'English' }
  , { code: 'nl', label: 'Nederlands (Dutch)' }
  , { code: 'de', label: 'Deutsch (German)' }
  , { code: 'es', label: 'Español (Spanish)' }
  , { code: 'fr', label: 'Français (French)' }
  ];

  //
  // 1:1 port of the $(".languageSelector").on("change", ...) handler.
  //
  onLanguageChange(languageCode: string): void
  {
    this.translate.use(languageCode.substring(0, 2));
    if (window.location.href.indexOf('preset') > -1)
    {
      setTimeout(() =>
      {
        document.title += ' | Get My Report';
      }, 100);
    }
  }
}
